import { DealDocument, DealDocumentType } from "../../../../api/generated-api";
import DoneIcon from "../../../common/icons/doneIcon";
import React, { ChangeEvent } from "react";
import {
  DealDocumentRowItem,
  DealDocumentRowItemProps,
} from "./DealDocumentRowItem";
import Button from "../../../common/forms/button";
import { toValidIdentifier } from "../../../../helpers/utilsTS";
import { ProgressIndicator } from "../../../common/progress/ProgressIndicator";
import { DKChip } from "../../../common/DKChip";
import { UploadDealDocumentFilePicker } from "../UploadDealDocumentFilePicker";

type COIDealDocumentRowItemProps = DealDocumentRowItemProps & {
  certificateOfIncorporation?: DealDocument;
  filedCertificateOfIncorporation?: DealDocument;
  onDocumentUploadFileSelected: (
    event: ChangeEvent<HTMLInputElement>,
    docType: DealDocumentType,
    docName: string
  ) => Promise<DealDocument>;
};
export const COIDealDocumentRowItem = (props: COIDealDocumentRowItemProps) => {
  const [showProgressIndicator, setShowProgressIndicator] =
    React.useState<boolean>(false);
  const [showUploadFilePicker, setShowUploadFilePicker] =
    React.useState<boolean>(false);

  const {
    certificateOfIncorporation,
    filedCertificateOfIncorporation,
    uploadable,
    ...rest
  } = props;

  //Render the delete button if:
  // * there is a COI but not a filed COI and props.deletable - or
  // * there is a filed COI and props.deletable
  const deletable =
    (certificateOfIncorporation &&
      !filedCertificateOfIncorporation &&
      props.deletable) ||
    (!!filedCertificateOfIncorporation && props.deletable);

  const buildPostNameAdornment = () => {
    return (
      <>
        {props.documentType === DealDocumentType.Certificate_of_Incorporation &&
          !filedCertificateOfIncorporation && (
            <DKChip label="Not yet filed" data-testid="coi-badge-not-filed" />
          )}
        {props.documentType === DealDocumentType.Certificate_of_Incorporation &&
          filedCertificateOfIncorporation && (
            <DKChip
              label="Filed"
              deleteIcon={<DoneIcon />}
              data-testid="coi-badge-filed"
            />
          )}
      </>
    );
  };

  const buildUploadFiledCOIButton = () => {
    return (
      <>
        {showProgressIndicator && (
          <ProgressIndicator
            size={20}
            dataTestId={
              "uploading-status-Filed_Certificate_of_Incorporation-icon"
            }
          />
        )}
        {props.documentType === DealDocumentType.Certificate_of_Incorporation &&
          certificateOfIncorporation &&
          !filedCertificateOfIncorporation &&
          uploadable && (
            <>
              {!showProgressIndicator && (
                <Button
                  data-testid={`add-${toValidIdentifier(
                    DealDocumentType.Filed_Certificate_of_Incorporation
                  )}-button`}
                  type="primary"
                  size="small"
                  onClick={() => {
                    setShowUploadFilePicker(true);
                  }}
                >
                  Upload Filed Copy
                </Button>
              )}

              <UploadDealDocumentFilePicker
                visible={showUploadFilePicker}
                dataTestId={`${toValidIdentifier(
                  DealDocumentType.Filed_Certificate_of_Incorporation
                )}-document-picker`}
                documentType={
                  DealDocumentType.Filed_Certificate_of_Incorporation
                }
                onDocumentSelect={async (event) => {
                  setShowUploadFilePicker(false);
                  setShowProgressIndicator(true);
                  const uploadedDocument =
                    await props.onDocumentUploadFileSelected(
                      event,
                      DealDocumentType.Filed_Certificate_of_Incorporation,
                      certificateOfIncorporation.name
                    );
                  setShowProgressIndicator(false);
                  return uploadedDocument;
                }}
                onDocumentSelected={() => {
                  setShowProgressIndicator(true);
                }}
                onDocumentFinishedUploading={() => {
                  setShowProgressIndicator(false);
                }}
                onCancel={() => setShowUploadFilePicker(false)}
              />
            </>
          )}
      </>
    );
  };

  return (
    <DealDocumentRowItem
      {...rest}
      dealDocument={
        filedCertificateOfIncorporation
          ? filedCertificateOfIncorporation
          : certificateOfIncorporation
      }
      deletable={deletable}
      uploadable={uploadable}
      postNameAdornment={buildPostNameAdornment()}
      extraRowAction={buildUploadFiledCOIButton()}
      documentType={
        filedCertificateOfIncorporation
          ? DealDocumentType.Filed_Certificate_of_Incorporation
          : DealDocumentType.Certificate_of_Incorporation
      }
    />
  );
};
